import { Outlet, Navigate, useLocation } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { useUnauthorizedInterceptor } from '../Services/InterceptorService';

/**
 * Guard — requires any authenticated user (role: 'user').
 * Admin is NOT allowed through here — they go to /admin.
 */
const Guard = ({ redirectPath = '/login' }) => {
    const prevLocation = useLocation();
    const { isAuthenticated, sessionChecked, user } = useSelector((state) => state.auth);
    useUnauthorizedInterceptor(true);

    if (!sessionChecked) return null; // wait for token verification before redirecting

    if (!isAuthenticated) {
        return <Navigate to={redirectPath} replace state={{ from: prevLocation }} />;
    }

    // Admins who somehow land here get redirected to the admin dashboard
    if (user?.role === 'admin') {
        return <Navigate to="/admin" replace />;
    }

    return <Outlet />;
};

/**
 * AdminGuard — requires role: 'admin'.
 * Regular users get redirected to their account screen.
 */
export const AdminGuard = ({ redirectPath = '/login' }) => {
    const prevLocation = useLocation();
    const { isAuthenticated, sessionChecked, user } = useSelector((state) => state.auth);
    useUnauthorizedInterceptor(true);

    if (!sessionChecked) return null;

    if (!isAuthenticated) {
        return <Navigate to={redirectPath} replace state={{ from: prevLocation }} />;
    }

    if (user?.role !== 'admin') {
        return <Navigate to="/home" replace />;
    }

    return <Outlet />;
};

/**
 * BlockAdminGuard — blocks admins, lets everyone else through (including guests).
 * Use for routes like /cart and /success where guests are welcome but admin has no business.
 */
export const BlockAdminGuard = () => {
    const { isAuthenticated, user } = useSelector((state) => state.auth);

    if (isAuthenticated && user?.role === 'admin') {
        return <Navigate to="/admin" replace />;
    }

    return <Outlet />;
};

export default Guard;
